/* SmartTalentHuntsRow — Sprint 3 (4 talent-discovery agent tiles)
 *   #6  Open-to-Work scanner · #7 GitHub-radar · #8 Alumni-tracker · #11 Boomerang-detector
 * Elke tile opent een inline configuratieform → agent draait → toont
 * 3-5 kandidaat-suggesties → picks belanden in Cronos. */
import React, { useState } from "react";
import i18n from "i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowUpRight, Building2, Check, Github, Loader2, Plus, Radar,
    RotateCcw, Sparkles, X,
} from "lucide-react";
import { LinkedInButton } from "@/components/common/LinkedInButton";

export default function SmartTalentHuntsRow({ onDone }) {
    const [active, setActive] = useState(null);   // "open_to_work" | ... | null
    const [busy, setBusy] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [ctx, setCtx] = useState({});
    const [picked, setPicked] = useState(new Set());
    const [form, setForm] = useState({ target_role: "", target_country: "BE", target_sector: "SaaS", tech_stack: "Python + FastAPI" });

    const TILES = [
        { variant: "open_to_work",   color: "#22c55e", icon: Radar,      label: "Open-to-Work scanner",  hint: i18n.t("search.tile_otw") },
        { variant: "github_radar",   color: "#a855f7", icon: Github,     label: "GitHub-radar",          hint: i18n.t("search.tile_github") },
        { variant: "alumni_tracker", color: "#eab308", icon: Building2,  label: "Alumni-tracker",        hint: i18n.t("search.tile_alumni") },
        { variant: "boomerang_hunt", color: "#ef4444", icon: RotateCcw,  label: "Boomerang-detector",    hint: i18n.t("search.tile_boomerang") },
    ];

    const runHunt = async (variant) => {
        setBusy(true); setSuggestions([]); setPicked(new Set());
        try {
            const r = await api.post("/network/talent-hunt", { variant, ...form, limit: 5 });
            setSuggestions(r.data?.suggestions || []);
            setCtx(r.data?.context || {});
            if ((r.data?.suggestions || []).length === 0) {
                toast.info(i18n.t("search.no_suggestions"));
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("search.agent_failed"));
        } finally { setBusy(false); }
    };

    const toggle = (i) => setPicked((p) => {
        const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n;
    });

    const promote = async () => {
        const picks = suggestions.filter((_, i) => picked.has(i));
        if (picks.length === 0) { toast.error(i18n.t("search.pick_min")); return; }
        setBusy(true);
        try {
            const r = await api.post("/network/talent-hunt/promote", { variant: active, suggestions: picks });
            toast.success(i18n.t("search.promoted", { count: r.data.count }));
            setActive(null); setSuggestions([]); setPicked(new Set());
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("search.add_failed"));
        } finally { setBusy(false); }
    };

    return (
        <div className="mb-4 rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4" data-testid="smart-talent-hunts">
            <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} className="text-[var(--jr-amber)]" />
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Slimme talent-hunts · autonome agents</span>
            </div>
            <div className="grid md:grid-cols-4 gap-3">
                {TILES.map((tile) => {
                    const on = active === tile.variant;
                    return (
                        <button key={tile.variant} onClick={() => { setActive(tile.variant); setSuggestions([]); }}
                            data-testid={`talent-tile-${tile.variant}`}
                            style={{ borderLeft: `3px solid ${tile.color}` }}
                            className={`text-left rounded-xl border p-3 transition-colors ${on ? "border-white/30 bg-[var(--jr-surface-2)]" : "border-white/10 bg-[var(--jr-surface-2)] hover:border-white/25"}`}>
                            <div className="flex items-center justify-between gap-2">
                                <tile.icon size={14} style={{ color: tile.color }} />
                                <ArrowUpRight size={12} className="text-[var(--jr-text-3)]"/>
                            </div>
                            <div className="mt-2 text-sm font-medium text-white">{tile.label}</div>
                            <div className="text-[11px] text-[var(--jr-text-2)] mt-1">{tile.hint}</div>
                        </button>
                    );
                })}
            </div>

            {/* Configuration & suggestions panel */}
            {active && (
                <div className="mt-4 rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-4" data-testid="talent-hunt-panel">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div className="text-sm text-white font-medium">
                            {TILES.find((t) => t.variant === active).label}
                        </div>
                        <button onClick={() => { setActive(null); setSuggestions([]); }}
                            className="text-xs text-[var(--jr-text-3)] hover:text-white p-1">
                            <X size={14}/>
                        </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                        <input value={form.target_role} onChange={(e) => setForm((f) => ({ ...f, target_role: e.target.value }))}
                            placeholder="Rol (bv. SRE)"
                            data-testid="talent-form-role"
                            className="text-xs bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-white placeholder:text-[var(--jr-text-3)] focus:border-[var(--jr-amber)] outline-none"/>
                        <input value={form.target_country} onChange={(e) => setForm((f) => ({ ...f, target_country: e.target.value }))}
                            placeholder="Land (BE)"
                            className="text-xs bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-white placeholder:text-[var(--jr-text-3)] focus:border-[var(--jr-amber)] outline-none"/>
                        {active === "github_radar" ? (
                            <input value={form.tech_stack} onChange={(e) => setForm((f) => ({ ...f, tech_stack: e.target.value }))}
                                placeholder="Tech stack"
                                className="text-xs bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-white placeholder:text-[var(--jr-text-3)] focus:border-[var(--jr-amber)] outline-none"/>
                        ) : (
                            <input value={form.target_sector} onChange={(e) => setForm((f) => ({ ...f, target_sector: e.target.value }))}
                                placeholder="Sector"
                                className="text-xs bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-white placeholder:text-[var(--jr-text-3)] focus:border-[var(--jr-amber)] outline-none"/>
                        )}
                        <button onClick={() => runHunt(active)} disabled={busy}
                            data-testid="talent-hunt-run"
                            className="text-xs px-3 py-1.5 rounded-full bg-[var(--jr-amber)] text-black font-medium hover:bg-[var(--jr-amber)]/85 disabled:opacity-50">
                            {busy ? <Loader2 size={11} className="animate-spin inline mr-1"/> : <Sparkles size={11} className="inline mr-1"/>}
                            {busy ? "Agent zoekt…" : "Start agent"}
                        </button>
                    </div>

                    {(active === "alumni_tracker" || active === "boomerang_hunt") && ctx.client_names && (
                        <div className="text-[11px] text-[var(--jr-text-3)] mb-2">Klanten in scope: {(ctx.client_names || []).join(" · ")}</div>
                    )}

                    {suggestions.length > 0 && (
                        <div className="space-y-2">
                            {suggestions.map((s, i) => {
                                const on = picked.has(i);
                                return (
                                    <button key={i} onClick={() => toggle(i)} type="button"
                                        data-testid={`talent-sug-${i}`}
                                        className={`w-full text-left rounded-lg border p-3 transition-colors ${on ? "border-[var(--jr-amber)] bg-[var(--jr-amber)]/5" : "border-white/10 bg-[var(--jr-surface)] hover:border-white/25"}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm text-white font-medium">{s.name}</div>
                                                <div className="text-xs text-[var(--jr-text-2)] mt-0.5">
                                                    {s.title || "?"} @ {s.company || "?"}
                                                    {s.previous_company && <span className="text-[var(--jr-text-3)]"> · ex-{s.previous_company}</span>}
                                                </div>
                                                {(s.availability_note || s.primary_language) && (
                                                    <div className="text-[10px] text-[var(--jr-amber)] mt-1 truncate">
                                                        {s.availability_note || `Primary: ${s.primary_language}`}
                                                    </div>
                                                )}
                                                <div className="text-[10px] text-[var(--jr-text-3)] mt-1 flex items-center gap-2 flex-wrap">
                                                    {s.location && <span>{s.location}</span>}
                                                    <LinkedInButton url={s.linkedin_url} name={s.name} variant="inline" resolve={s.id ? { type: "candidate", id: s.id } : undefined} />
                                                    {s.github_url && <a href={s.github_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[#a855f7]"><Github size={9}/>{s.github_username || "GitHub"}</a>}
                                                </div>
                                            </div>
                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${on ? "bg-[var(--jr-amber)] border-[var(--jr-amber)]" : "border-white/20"}`}>
                                                {on && <Check size={12} className="text-black"/>}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                            <div className="flex justify-end pt-2">
                                <button onClick={promote} disabled={busy || picked.size === 0}
                                    data-testid="talent-hunt-promote"
                                    className="text-xs px-4 py-1.5 rounded-full bg-[var(--jr-amber)] text-black font-medium hover:bg-[var(--jr-amber)]/85 disabled:opacity-50">
                                    {busy ? <Loader2 size={11} className="animate-spin inline mr-1"/> : <Plus size={11} className="inline mr-1"/>}
                                    Voeg {picked.size} toe aan Cronos
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
